/**
 * @file 게시판 글 작성·수정·답글 — 한 다이얼로그가 세 모드를 겸한다.
 *
 * <p>{@code mode} 로 갈린다: {@code "create"} · {@code "edit"} · {@code "reply"}.
 * 세 화면의 입력 항목이 제목·본문·첨부로 같아 따로 만들면 세 곳을 같이 고쳐야 한다.
 *
 * <h3>첨부는 저장 후에 올라간다</h3>
 * <p>서버가 첨부를 {@code postId} 에 붙이기 때문에 글이 먼저 만들어져야 한다. 그래서 작성 모드에서는
 * <b>글 저장 → 파일 순차 업로드</b> 순으로 진행하고, 업로드가 실패해도 글은 남는다(그 사실을 알린다).
 */
import React, { useEffect, useState } from "react";
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    IconButton, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ActionButton from "../../../component/common/ActionButton";
import FormField from "../../../component/common/FormField";
import RichEditor from "../../../component/common/RichTextEditor";
import { boardApi } from "../api/boardApi";
import { notify } from "../../../util/toast";
import { getApiErrorMessage } from "../../../util/apiFeedback";
import { formatFileSize } from "../../../util/numberFormat";
import { C, fileTypeIcon } from "../boardShared";

export default function BoardPostComposeDialog({
    open, onClose, mode = "create", boardType, post, isAdmin, onSaved,
}) {
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [notice, setNotice] = useState(false);
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false);
    /** RichEditor 는 uncontrolled(initialValue) 라 열 때마다 key 를 바꿔 내용을 갈아준다. */
    const [editorKey, setEditorKey] = useState(0);

    useEffect(() => {
        if (!open) return;
        if (mode === "edit" && post) {
            setTitle(post.title || "");
            setContent(post.content || "");
            setNotice(Boolean(post.notice));
        } else if (mode === "reply" && post) {
            // 답글 제목은 원글 제목에 [답글] 을 붙여 미리 채운다 — 대부분 그대로 쓴다.
            setTitle(`[답글] ${post.title || ""}`.slice(0, 300));
            setContent("");
            setNotice(false);
        } else {
            setTitle(""); setContent(""); setNotice(false);
        }
        setFiles([]);
        setEditorKey(k => k + 1);
    }, [open, mode, post]);

    const heading = mode === "edit" ? "글 수정" : mode === "reply" ? "답글 작성" : "새 글 작성";

    const handleSave = async () => {
        const t = title.trim();
        if (!t) { notify("제목을 입력하세요", "error"); return; }
        if (saving) return;
        setSaving(true);
        try {
            const body = { title: t, content, notice: isAdmin ? notice : undefined };
            let saved;
            if (mode === "edit") {
                saved = await boardApi.updatePost(post.id, body);
            } else {
                const typeId = mode === "reply" ? post.boardTypeId : boardType.id;
                saved = await boardApi.createPost(typeId, {
                    ...body,
                    parentId: mode === "reply" ? post.id : undefined,
                });
            }

            // 첨부 업로드 — 글 저장이 끝난 뒤라야 postId 가 있다.
            let failed = 0;
            for (const f of files) {
                try { await boardApi.uploadAttachment(saved.id, f); }
                catch { failed++; }
            }
            if (failed > 0) {
                notify(`글은 저장됐지만 첨부 ${failed}개를 올리지 못했습니다. 상세에서 다시 첨부해 주세요.`, "warning");
            } else {
                notify(mode === "edit" ? "글이 수정되었습니다" : "글이 등록되었습니다", "success");
            }
            onSaved?.(saved);
            onClose();
        } catch (err) {
            notify(getApiErrorMessage(err, "글 저장 실패"), "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
                {heading}
                {boardType && (
                    <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.textMuted, ml: 1 }}>
                        {mode === "reply" ? post?.boardTypeName : boardType.name}
                    </Box>
                )}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={2}>
                    {mode === "reply" && post && (
                        <Box sx={{
                            px: 1.5, py: 1, borderRadius: 1, bgcolor: C.bg,
                            border: `1px solid ${C.borderLight}`,
                        }}>
                            <Typography sx={{ fontSize: 12, color: C.textMuted }}>원글</Typography>
                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
                                {post.title}
                            </Typography>
                        </Box>
                    )}

                    <FormField label="제목" required>
                        <TextField
                            size="small" fullWidth
                            value={title}
                            onChange={e => setTitle(e.target.value.slice(0, 300))}
                            placeholder="제목을 입력하세요"
                        />
                    </FormField>

                    <FormField label="내용">
                        <RichEditor
                            key={editorKey}
                            initialValue={content}
                            onChange={setContent}
                            placeholder="내용을 입력하세요..."
                            minHeight={260}
                            /*
                             * 본문에 붙여넣은 이미지도 게시판 첨부로 올린다 — 에디터가 base64 로 품으면
                             * 본문 컬럼이 수 MB 로 불어나 목록 검색까지 느려진다. 다만 이 시점에는 postId 가
                             * 없어(글 저장 전) 업로드할 대상이 없으므로 편집 모드에서만 지원한다.
                             */
                            onUploadImage={mode === "edit" && post?.id
                                ? async (file) => {
                                    try {
                                        const r = await boardApi.uploadAttachment(post.id, file);
                                        return r.fileUrl;
                                    } catch { return null; }
                                }
                                : undefined}
                        />
                    </FormField>

                    {/* 공지 고정은 관리자만 — 서버도 비관리자의 notice 값을 무시한다. */}
                    {isAdmin && mode !== "reply" && (
                        <FormControlLabel
                            control={<Switch size="small" checked={notice} onChange={e => setNotice(e.target.checked)} />}
                            label={
                                <Typography sx={{ fontSize: 13 }}>
                                    공지로 고정
                                    <Box component="span" sx={{ color: C.textMuted, ml: 0.75 }}>
                                        목록 최상단에 [공지]로 표시됩니다
                                    </Box>
                                </Typography>
                            }
                        />
                    )}

                    <FormField label="첨부파일">
                        <Box>
                            <ActionButton variant="secondary"
                                startIcon={<AttachFileRoundedIcon sx={{ fontSize: 15 }} />}
                                onClick={() => document.getElementById("board-compose-file")?.click()}
                                sx={{ fontSize: 12.5 }} label="파일 선택" />
                            <input id="board-compose-file" type="file" hidden multiple
                                onChange={e => {
                                    setFiles(p => [...p, ...Array.from(e.target.files)]);
                                    e.target.value = "";
                                }} />
                            {files.length > 0 && (
                                <Stack spacing={0.5} mt={1}>
                                    {files.map((f, i) => (
                                        <Stack key={i} direction="row" alignItems="center" spacing={1}
                                            sx={{
                                                px: 1.25, py: 0.65, borderRadius: 1,
                                                border: `1px solid ${C.borderLight}`, bgcolor: C.bg,
                                            }}>
                                            {fileTypeIcon({ originalFileName: f.name, contentType: f.type }, 16)}
                                            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.textPrimary }}>
                                                {f.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: C.textMuted, flexShrink: 0 }}>
                                                {formatFileSize(f.size)}
                                            </Typography>
                                            <Tooltip title="목록에서 제거">
                                                <IconButton size="small" sx={{ p: 0.2 }}
                                                    onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                                                    <CloseRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                            {mode === "edit" && (
                                <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: 0.75 }}>
                                    * 기존 첨부는 상세 화면에서 삭제할 수 있습니다.
                                </Typography>
                            )}
                        </Box>
                    </FormField>
                </Stack>
            </DialogContent>
            <DialogActions>
                <ActionButton variant="cancel" onClick={onClose} disabled={saving} />
                <ActionButton variant="save" onClick={handleSave} loading={saving}
                    label={mode === "edit" ? "저장" : "등록"} />
            </DialogActions>
        </Dialog>
    );
}
